/**
 * 本机节点面板：所有线路都连不上时，把这台设备变成局域网里的会议节点。
 *
 * 摆在登录页而不是会中：这是「连不上服务器」时的出路，那时用户还没进会。
 * 平时完全不显示 —— 只在能当节点的 App（桌面、iOS）且线路全断时才出现，否则会诱导用户
 * 在网络正常时开一个多余的节点。
 */

import { useEffect, useState } from 'react'

import { isMobileApp } from '../lib/desktop'
import { canHostNode, nodeStatus, onNodeStatus, startNode, stopNode, type NodeStatus } from '../lib/localNode'

export function LocalNode({ visible }: { visible: boolean }) {
  const [st, setSt] = useState<NodeStatus>({ running: false })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const [can, setCan] = useState(false)

  useEffect(() => {
    let off: (() => void) | undefined
    let alive = true
    void (async () => {
      // 能不能当节点问原生侧：桌面与 iOS 能，安卓和网页版不能。
      if (!(await canHostNode()) || !alive) return
      setCan(true)
      // 先读一次当前状态：面板可能是在节点已经跑起来之后才显示的
      // （用户关掉面板又打开、或者线路恢复后又断）。
      const s = await nodeStatus()
      if (alive) setSt(s)
      const f = await onNodeStatus((next) => { if (alive) setSt(next) })
      // 订阅建立前组件就卸载了：立刻退订，否则监听会一直挂着。
      if (alive) off = f
      else f()
    })()
    return () => { alive = false; off?.() }
  }, [])

  if (!can || !visible) return null

  // 同一个面板，手机与电脑只差措辞和一条前台提示。
  const device = isMobileApp() ? '手机' : '电脑'

  const act = async (fn: () => Promise<NodeStatus>) => {
    setBusy(true)
    setErr('')
    try {
      setSt(await fn())
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="local-node">
      <div className="ln-title">连不上服务器？用这台{device}开会</div>
      {device === '手机' && (
        <p className="ln-hint">
          手机当节点时请让本 App 一直停在前台、不要锁屏：切到后台系统会暂停它，所有人会同时掉线。
        </p>
      )}
      {!st.running ? (
        <>
          <p className="ln-hint">
            同一局域网里的人用浏览器就能进，不必装任何东西。
            会议数据只在本机内存里，这台{device}关掉就什么都不留。
          </p>
          <button type="button" className="primary block" disabled={busy} onClick={() => void act(() => startNode())}>
            {busy ? '正在启动…' : `把这台${device}当会议节点`}
          </button>
        </>
      ) : (
        <>
          <dl className="ln-info">
            <dt>入会地址</dt>
            <dd><code>{st.url ?? '—'}</code></dd>
            <dt>会议号</dt>
            <dd>{st.room ?? '正在准备…'}</dd>
            <dt>主持人码</dt>
            <dd><code>{st.hostCode ?? '正在准备…'}</code></dd>
            <dt>参会者码</dt>
            <dd><code>{st.guestCode ?? '正在准备…'}</code></dd>
          </dl>
          <p className="ln-hint">
            {/* 自签证书换来的是「能不能发言」：不走 HTTPS 的话浏览器根本不给麦克风。
                这句提示必须显眼 —— 参会者看到警告页的第一反应是关掉页面。 */}
            <strong>告诉大家：</strong>浏览器第一次会提示「连接不是私密连接」，
            点「高级 → 继续前往」即可。不点就开不了麦克风和摄像头。
          </p>
          <p className="ln-hint">
            两个码不要混发：拿到主持人码的人可以踢人、结束会议。
            {/* 地址必须显示出来让人核对：多网卡机器上探测到的可能不是同事所在的那个网段。 */}
            地址请对照自己{device}的网络设置确认 —— 多网卡时可能需要换一个。
          </p>
          <button type="button" className="block" disabled={busy} onClick={() => void act(stopNode)}>
            {busy ? '正在停止…' : `停止节点${st.pid ? `（进程 ${st.pid}）` : ''}`}
          </button>
        </>
      )}
      {(err || st.error) && <div className="login-err">{err || st.error}</div>}
    </div>
  )
}
